import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Container, Skeleton, Stack, Typography } from '@mui/material';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSnackbar } from 'notistack';
import { defaultBreakpoints, defaultWeights } from '../opportunity_calculator/indicatorConfig';
import { getRiskDataset } from './datasets/riskDatasets';
import { buildRiskIndicators } from './riskIndicatorConfig';
import { DEFAULT_TIER_THRESHOLDS, scoreRiskSchools } from './riskTiers';
import { filterSchools, geographyOptions } from './riskFilters';
import { DEFAULT_PROXIMITY_MILES, proximityPairs } from './proximity';
import { parseRiskSearch, serializeRiskSearch } from './riskUrlState';
import useRiskCalculatorData from './useRiskCalculatorData';
import RiskFilterBar from './RiskFilterBar';
import RiskModelBar from './RiskModelBar';
import RiskRankingTable from './RiskRankingTable';
import RiskInsightCards from './RiskInsightCards';
import SchoolRiskProfile from './SchoolRiskProfile';
import OpportunityCalculatorTour from '../opportunity_calculator/OpportunityCalculatorTour';
import {
  readRiskTourDismissed,
  riskTourSteps,
  shouldShowRiskTour,
  writeRiskTourDismissed,
} from './riskCalculatorTour';

const numericBreakpoints = (breakpointsById, indicators) =>
  Object.fromEntries(
    indicators.map((indicator) => {
      const values = breakpointsById[indicator.id] || indicator.breakpoints;
      return [
        indicator.id,
        values.map((value, index) => (Number.isFinite(Number(value)) && value !== '' ? Number(value) : indicator.breakpoints[index])),
      ];
    })
  );

const RiskCalculator = ({ embedded = false, locationId, onShare, isFullscreen = false, riskMapPath = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const dataset = useMemo(() => getRiskDataset(locationId), [locationId]);
  const { loading, error, meta, charters, peers } = useRiskCalculatorData(locationId, {
    enabled: Boolean(dataset),
  });
  const indicators = useMemo(() => buildRiskIndicators(dataset, meta), [dataset, meta]);
  const geographies = dataset?.geographies || [];

  const [initial] = useState(() => parseRiskSearch(searchParams, indicators, geographies));
  const [weights, setWeights] = useState(initial.weights);
  const [breakpointsById, setBreakpointsById] = useState(initial.breakpointsById);
  const [thresholds, setThresholds] = useState(initial.thresholds);
  const [renormalize, setRenormalize] = useState(initial.renormalize);
  const [geoLevel, setGeoLevel] = useState(initial.geoLevel);
  const [geoValue, setGeoValue] = useState(initial.geoValue);
  const [steadyMode, setSteadyMode] = useState(initial.steadyMode);
  const [gradeBand, setGradeBand] = useState(initial.gradeBand);
  const [modelOpen, setModelOpen] = useState(false);
  // `?rschool=<id>` (from the risk map's pin card) opens that school's profile.
  const [deepLinkId] = useState(() => searchParams.get('rschool'));
  const [selectedId, setSelectedId] = useState(deepLinkId);
  const [proximityMiles, setProximityMiles] = useState(DEFAULT_PROXIMITY_MILES);
  const [tourStep, setTourStep] = useState(0);
  const [tourDismissed, setTourDismissed] = useState(() => readRiskTourDismissed() || Boolean(deepLinkId));
  const [tourReplay, setTourReplay] = useState(false);
  const tourSteps = useMemo(() => riskTourSteps(dataset), [dataset]);
  const showingTour = shouldShowRiskTour({
    dismissed: tourDismissed,
    replaying: tourReplay,
    isFullscreen,
    ready: Boolean(dataset) && !loading && !error,
  });

  const scoringBreakpoints = useMemo(
    () => numericBreakpoints(breakpointsById, indicators),
    [breakpointsById, indicators]
  );

  useEffect(() => {
    const next = serializeRiskSearch({
      indicators,
      weights,
      breakpointsById: scoringBreakpoints,
      thresholds,
      geoLevel,
      geoValue,
      steadyMode,
      gradeBand,
      renormalize,
    });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [indicators, weights, scoringBreakpoints, thresholds, geoLevel, geoValue, steadyMode, gradeBand, renormalize, searchParams, setSearchParams]);

  const scoreOptions = useMemo(
    () => ({ thresholds, renormalize, breakpointsById: scoringBreakpoints }),
    [thresholds, renormalize, scoringBreakpoints]
  );

  const level = geographies.find((g) => g.id === geoLevel);
  const geoOptions = useMemo(() => geographyOptions(charters, level), [charters, level]);

  const filteredCharters = useMemo(
    () => filterSchools(charters, { level, value: geoValue, steadyMode, gradeBand }),
    [charters, level, geoValue, steadyMode, gradeBand]
  );
  const rows = useMemo(
    () => scoreRiskSchools(filteredCharters, indicators, weights, scoreOptions),
    [filteredCharters, indicators, weights, scoreOptions]
  );
  const allCharterRows = useMemo(
    () => scoreRiskSchools(charters, indicators, weights, scoreOptions),
    [charters, indicators, weights, scoreOptions]
  );
  const peerRows = useMemo(
    () => scoreRiskSchools(peers, indicators, weights, scoreOptions),
    [peers, indicators, weights, scoreOptions]
  );
  const peerRowsById = useMemo(() => new Map(peerRows.map((row) => [row.school.id, row])), [peerRows]);

  // Pair the filtered high-risk charters with low-risk schools anywhere nearby.
  const pairs = useMemo(() => {
    const filteredIds = new Set(rows.map((row) => row.school.id));
    return proximityPairs(allCharterRows, peerRows, proximityMiles).filter((pair) =>
      filteredIds.has(pair.row.school.id)
    );
  }, [rows, allCharterRows, peerRows, proximityMiles]);

  const selectedRow = useMemo(
    () => (selectedId ? rows.find((row) => String(row.school.id) === String(selectedId)) || null : null),
    [rows, selectedId]
  );

  const areaName = geoLevel !== 'state' && geoValue ? geoValue : dataset?.regionName || 'this area';

  const goToTourStep = (nextStep) => {
    setTourStep(nextStep);
    setModelOpen(Boolean(tourSteps[nextStep]?.expandScoring));
  };

  const dismissTour = () => {
    writeRiskTourDismissed();
    setTourDismissed(true);
    setTourReplay(false);
    setTourStep(0);
    setModelOpen(false);
  };

  const replayTour = () => {
    setTourStep(0);
    setTourReplay(true);
    setModelOpen(false);
  };

  const copyLink = async () => {
    if (onShare) {
      onShare();
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      enqueueSnackbar('Link copied — weights, tiers, and filters are in the URL.', { variant: 'success' });
    } catch (_err) {
      enqueueSnackbar('Could not copy the link.', { variant: 'error' });
    }
  };

  if (!dataset) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">The Risk Calculator is not available for this location yet.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: embedded ? { xs: 2, md: 3 } : 0, py: embedded ? 2.5 : 0 }}>
      <Container maxWidth="xl" disableGutters={embedded}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'flex-start' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            {!embedded && (
              <Typography variant="h4" fontWeight={700}>
                Risk Calculator
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              Which {dataset.regionName} charter schools face the greatest long-term enrollment risk?
              Scores blend enrollment size and trend, distance from peak, entry-grade and cohort
              retention, academic quality, and area and projected outlook.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button size="small" variant="outlined" startIcon={<LinkOutlinedIcon />} onClick={copyLink}>
              {onShare ? 'Share calculator' : 'Copy shareable link'}
            </Button>
            {tourDismissed && !showingTour && (
              <Button
                size="small"
                startIcon={<HelpOutlineIcon />}
                onClick={replayTour}
                aria-label="Replay calculator tour"
                sx={{ textTransform: 'none' }}
              >
                Tour
              </Button>
            )}
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box data-calculator-tour="filters">
        <RiskFilterBar
          geographies={geographies}
          geoLevel={geoLevel}
          onGeoLevel={(next) => {
            setGeoLevel(next);
            setGeoValue('');
          }}
          geoValue={geoValue}
          onGeoValue={setGeoValue}
          geoOptions={geoOptions}
          steadyMode={steadyMode}
          onSteadyMode={setSteadyMode}
          gradeBand={gradeBand}
          onGradeBand={setGradeBand}
          shownCount={filteredCharters.length}
          totalCount={charters.length}
        />
        </Box>

        {loading ? (
          <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
        ) : (
          <>
            <Box data-calculator-tour="insights">
              <RiskInsightCards
                rows={rows}
                areaName={areaName}
                pairs={pairs}
                proximityMiles={proximityMiles}
                onProximityMiles={setProximityMiles}
                onSelect={(row) => setSelectedId(row.school.id)}
              />
            </Box>
            <Box data-calculator-tour="scoring">
            <RiskModelBar
              open={modelOpen}
              onToggle={() => setModelOpen((current) => !current)}
              indicators={indicators}
              weights={weights}
              onWeightChange={(id, value) => setWeights((current) => ({ ...current, [id]: value }))}
              onResetWeights={() => setWeights(defaultWeights(indicators))}
              thresholds={thresholds}
              onThresholdChange={(key, value) =>
                setThresholds((current) =>
                  key === 'reset' ? { ...DEFAULT_TIER_THRESHOLDS } : { ...current, [key]: value }
                )
              }
              renormalize={renormalize}
              onRenormalize={setRenormalize}
              breakpointsById={breakpointsById}
              onBreakpointChange={(id, values) => setBreakpointsById((current) => ({ ...current, [id]: values }))}
              onBreakpointReset={() => setBreakpointsById(defaultBreakpoints(indicators))}
            />
            </Box>
            <Box data-calculator-tour="table">
              <RiskRankingTable
                rows={rows}
                indicators={indicators}
                csvPrefix={dataset.csvPrefix}
                onSelect={(row) => setSelectedId(row.school.id)}
                selectedId={selectedId}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {dataset.qualityMethodology} Enrollment {meta.peakStartYear || '2019-2020'} to{' '}
              {meta.currentYear || 'latest'}; ProjectX midpoint through {meta.futureYear || '5 years out'}; area
              population (ESRI K-12) {meta.esriYear} to {meta.esriFutureYear}.
            </Typography>
          </>
        )}

        <SchoolRiskProfile
          row={selectedRow}
          indicators={indicators}
          peerRowsById={peerRowsById}
          pair={pairs.find((pair) => String(pair.row.school.id) === String(selectedId)) || null}
          meta={meta}
          qualityMethodology={dataset.qualityMethodology}
          mapPath={riskMapPath}
          onClose={() => setSelectedId(null)}
        />
        {showingTour && (
          <OpportunityCalculatorTour
            steps={tourSteps}
            step={tourStep}
            onStepChange={goToTourStep}
            onDismiss={dismissTour}
            layoutKey={modelOpen}
            testId="risk-tour"
          />
        )}
      </Container>
    </Box>
  );
};

export default RiskCalculator;
